import { Reveal, Eyebrow } from "@/components/site/Motion";
import { Tags } from "@/components/solution/Sections";
import { STORY, BELIEF, TOMORROW, VISION, MISSION, VALUES, FOUNDER } from "@/data/about";

const Body = ({ children }) => <p className="text-base leading-relaxed text-[#2D6A64] md:text-lg">{children}</p>;
const WRAP = "mx-auto max-w-[1400px] px-6 lg:px-12";

export const Story = () => (
  <section className={`${WRAP} py-24 lg:py-36`} data-testid="about-story">
    <Reveal>
      <blockquote className="font-display max-w-5xl border-l-2 border-[#D4AF37] pl-6 text-3xl font-semibold leading-[1.15] tracking-tight text-[#0D9488] sm:text-4xl lg:pl-10 lg:text-6xl" data-testid="about-quote">
        {STORY.quote}
      </blockquote>
    </Reveal>
    <div className="mt-16 grid gap-10 lg:mt-24 lg:grid-cols-12">
      <Reveal className="lg:col-span-6 lg:col-start-7">
        <Body>{STORY.context}</Body>
        <p className="font-display mt-10 text-2xl font-bold tracking-tight text-[#0D9488] lg:text-3xl" data-testid="about-gap">{STORY.gap}</p>
      </Reveal>
    </div>
  </section>
);

export const Belief = () => (
  <section className="border-y border-[#E2ECEB] bg-[#F4F9F8]" data-testid="about-belief">
    <div className={`${WRAP} grid gap-12 py-24 lg:grid-cols-12 lg:py-32`}>
      <Reveal className="lg:col-span-6">
        <Eyebrow>Our Belief</Eyebrow>
        <h2 className="font-display mt-6 text-3xl font-bold tracking-tight text-[#0D9488] sm:text-4xl lg:text-5xl">{BELIEF.title}</h2>
      </Reveal>
      <Reveal delay={0.1} className="space-y-6 lg:col-span-5 lg:col-start-8 lg:pt-14">
        <Body>{BELIEF.lead}</Body>
        <p className="font-display border-l border-[#D4AF37] pl-5 text-xl font-semibold text-[#0D9488]">{BELIEF.heart}</p>
        {BELIEF.paras.map((p) => <Body key={p}>{p}</Body>)}
      </Reveal>
    </div>
  </section>
);

export const Tomorrow = () => (
  <section className="relative overflow-hidden bg-[#074E48] text-white grain" data-testid="about-tomorrow">
    <div className={`${WRAP} relative py-24 lg:py-40`}>
      <Reveal>
        <p className="font-display max-w-5xl text-3xl font-bold leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">{TOMORROW.statement}</p>
      </Reveal>
      <Reveal delay={0.1} className="mt-12 grid gap-8 lg:grid-cols-12">
        <p className="text-lg text-[#CFE7E3] lg:col-span-5">{TOMORROW.follow}</p>
        <div className="lg:col-span-5 lg:col-start-8">
          {TOMORROW.close.map((c) => (
            <p key={c} className="font-display text-xl font-semibold text-[#F7EFD2] lg:text-2xl">{c}</p>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const VMCard = ({ label, text, testId }) => (
  <div className="group relative h-full border border-[#D4AF37]/50 bg-white p-8 transition-shadow duration-500 hover:shadow-[0_30px_60px_-30px_rgba(7,78,72,0.35)] lg:p-12" data-testid={testId}>
    <span className="absolute left-0 top-0 h-px w-16 bg-[#D4AF37] transition-[width] duration-500 group-hover:w-full" />
    <Eyebrow>{label}</Eyebrow>
    <p className="font-display mt-8 text-xl font-semibold leading-snug text-[#0D9488] sm:text-2xl lg:text-3xl">{text}</p>
  </div>
);

export const VisionMission = () => (
  <section className={`${WRAP} grid gap-6 py-24 md:grid-cols-2 lg:py-32`} data-testid="about-vision-mission">
    <Reveal><VMCard label="Vision" text={VISION} testId="about-vision" /></Reveal>
    <Reveal delay={0.1}><VMCard label="Mission" text={MISSION} testId="about-mission" /></Reveal>
  </section>
);

const ValueCard = ({ v, i }) => (
  <Reveal delay={(i % 3) * 0.08} className="h-full">
    <article className="group relative h-full overflow-hidden border border-[#E2ECEB] bg-white p-8 transition-[border-color] duration-500 hover:border-[#D4AF37] lg:p-10" data-testid={`value-${v.l}`}>
      <span className="font-display pointer-events-none absolute -right-2 -top-6 select-none text-[9rem] font-extrabold leading-none text-[#D4AF37]/15 transition-colors duration-500 group-hover:text-[#D4AF37]/30" aria-hidden>{v.l}</span>
      <span className="font-display relative text-xs font-bold uppercase tracking-[0.3em] text-[#0D9488]">0{i + 1} · {v.name}</span>
      <h3 className="font-display relative mt-6 text-xl font-bold tracking-tight text-[#0D9488] lg:text-2xl">{v.headline}</h3>
      <p className="relative mt-4 text-base leading-relaxed text-[#2D6A64]">{v.text}</p>
      {v.tag && <p className="font-display relative mt-5 font-semibold text-[#0F3D39]">{v.tag}</p>}
    </article>
  </Reveal>
);

export const Values = () => (
  <section className="border-t border-[#E2ECEB]" data-testid="about-values">
    <div className={`${WRAP} py-24 lg:py-32`}>
      <Reveal className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Our Values</Eyebrow>
          <h2 className="font-display mt-6 flex text-6xl font-extrabold tracking-[0.12em] text-[#0D9488] sm:text-7xl lg:text-8xl" data-testid="wisdom-word">
            {VALUES.map((v) => (
              <span key={v.l} className="transition-colors duration-300 hover:text-[#D4AF37]">{v.l}</span>
            ))}
          </h2>
        </div>
        <p className="text-base leading-relaxed text-[#2D6A64] md:text-lg lg:col-span-4 lg:col-start-9">The behaviours that make The Krimzon Promise possible.</p>
      </Reveal>
      <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {VALUES.map((v, i) => <ValueCard key={v.l} v={v} i={i} />)}
      </div>
    </div>
  </section>
);

export const Founder = () => (
  <section className="border-t border-[#E2ECEB] bg-[#F4F9F8]" data-testid="about-founder">
    <div className={`${WRAP} grid gap-14 py-24 lg:grid-cols-12 lg:py-32`}>
      <Reveal className="lg:col-span-4">
        <div className="relative mx-auto max-w-[360px]">
          <div className="absolute inset-0 translate-x-3 translate-y-3 border border-[#D4AF37]/60" aria-hidden />
          <div className="clip-corner relative aspect-[4/5] overflow-hidden bg-[#074E48]" data-testid="founder-photo">
            <img src="/img/founder.jpg" alt="Nilesh Mhatre, Founder of KRIMZON" className="h-full w-full object-cover object-[62%_30%]" />
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
        <Eyebrow>Founder</Eyebrow>
        <h2 className="font-display mt-6 text-4xl font-bold tracking-tight text-[#0D9488] lg:text-5xl" data-testid="founder-name">{FOUNDER.name}</h2>
        <p className="font-display mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#0F3D39]">{FOUNDER.role}</p>
        <div className="mt-10 space-y-5">
          {FOUNDER.paras.map((p) => <Body key={p}>{p}</Body>)}
        </div>
        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-[#0D9488]">Areas of expertise</p>
        <div className="mt-4"><Tags items={FOUNDER.expertise} /></div>
      </Reveal>
    </div>
  </section>
);
